import { MapPin, Wifi, Flame, Mountain, Star } from "lucide-react";
import { ASSETS, CONTACT } from "@/lib/site";

const TAGS = [
  { icon: Wifi, label: "Free Wi-Fi" },
  { icon: Flame, label: "Bonfire Nights" },
  { icon: Mountain, label: "Mountain Views" },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden sh-grain"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={ASSETS.hero}
          alt="Sardar House at dusk in Nathia Gali"
          className="w-full h-full object-cover scale-105"
          loading="eager"
          fetchpriority="high"
        />
        <div className="absolute inset-0 sh-hero-mask" />
        {/* warm amber lamp glow */}
        <div className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full bg-[color:var(--sh-amber-glow)] blur-3xl opacity-70 pointer-events-none" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 pt-40 sm:pt-44 lg:pt-52 pb-24 min-h-[100svh] flex flex-col">
        {/* small eyebrow */}
        <div
          data-testid="hero-eyebrow"
          className="flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-[color:var(--sh-cream-dim)] mb-6"
        >
          <span className="w-8 h-px bg-[color:var(--sh-amber)]" />
          Nathia Gali · Est. in the hills
        </div>

        {/* Headline */}
        <h1
          data-testid="hero-headline"
          className="sh-display text-[color:var(--sh-cream)] text-[15vw] sm:text-[10vw] lg:text-[8.2vw] xl:text-[7.5rem] font-medium max-w-6xl"
        >
          Sardar<br />
          House<span className="sh-dot">.</span>
        </h1>

        {/* Subtext */}
        <p
          data-testid="hero-subtext"
          className="mt-6 sm:mt-8 max-w-xl text-[color:var(--sh-cream-dim)] text-base sm:text-lg leading-relaxed"
        >
          A boutique mountain retreat tucked into the pine forests of Nathia Gali —
          warm rooms, cold air, and evenings around the bonfire. Come for the
          quiet, stay for the view.
        </p>

        {/* CTA row */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href="#book"
            data-testid="hero-book-btn"
            className="sh-amber-btn inline-flex items-center gap-2 px-7 py-4 text-sm"
          >
            Book Your Stay
            <span aria-hidden>→</span>
          </a>
          <a
            href="#rooms"
            data-testid="hero-rooms-btn"
            className="sh-ghost-btn inline-flex items-center gap-2 px-6 py-4 text-sm"
          >
            Explore Rooms
          </a>
        </div>

        {/* Spacer to push info strip to bottom */}
        <div className="flex-1" />

        {/* Floating info strip */}
        <div
          data-testid="hero-info-strip"
          className="mt-14 sm:mt-20 sh-nav-pill rounded-2xl sm:rounded-full px-5 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6"
        >
          <div className="flex items-center gap-2 text-sm text-[color:var(--sh-cream)]">
            <MapPin size={16} className="text-[color:var(--sh-amber)]" strokeWidth={1.6} />
            <span>{CONTACT.address}</span>
          </div>
          <div className="hidden sm:block w-px h-5 bg-[color:var(--sh-line)]" />
          <div className="flex items-center gap-4 flex-wrap">
            {TAGS.map((t) => (
              <div
                key={t.label}
                className="flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--sh-cream-dim)]"
              >
                <t.icon size={14} className="text-[color:var(--sh-amber)]" strokeWidth={1.4} />
                {t.label}
              </div>
            ))}
          </div>
          <div className="sm:ml-auto flex items-center gap-1.5 text-xs text-[color:var(--sh-cream)]">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                size={12}
                className="text-[color:var(--sh-amber)]"
                fill="currentColor"
                strokeWidth={0}
              />
            ))}
            <span className="ml-2 text-[color:var(--sh-cream-dim)]">Guest favourite</span>
          </div>
        </div>
      </div>
    </section>
  );
}
